import React, { useState, useEffect } from 'react';
import { FaDownload, FaTimes, FaMobile } from 'react-icons/fa';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if app is already installed (standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone || 
                             document.referrer.includes('android-app://');
    setIsStandalone(isInStandaloneMode);

    // Check if user has already dismissed the prompt
    const hasBeenDismissed = localStorage.getItem('pwa-prompt-dismissed');
    
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: beforeinstallprompt event fired');
      e.preventDefault(); // Prevent the mini-infobar from appearing
      setDeferredPrompt(e);
      
      // Show the install prompt if it hasn't been dismissed and not in standalone
      if (!hasBeenDismissed && !isInStandaloneMode) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 3000); // Show after 3 seconds
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA: App was installed');
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS devices, show manual install instructions
    if (iOS && !isInStandaloneMode && !hasBeenDismissed) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000); // Show after 5 seconds on iOS
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA: User accepted the install prompt');
      } else {
        console.log('PWA: User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
    
    // Remove dismissal after 7 days
    setTimeout(() => {
      localStorage.removeItem('pwa-prompt-dismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  // Don't show if already installed or dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className={`pwa-install-prompt ${showPrompt ? 'show' : ''}`}>
      <div className="pwa-prompt-content">
        <button 
          className="pwa-prompt-close" 
          onClick={handleDismiss}
          aria-label="Close install prompt"
        >
          <FaTimes />
        </button>
        
        <div className="pwa-prompt-icon">
          <FaMobile />
        </div>
        
        <div className="pwa-prompt-text">
          <h3>Install YJC Wellness</h3>
          <p>
            {isIOS 
              ? 'Tap the share button and select "Add to Home Screen" for quick access'
              : 'Add to your home screen for quick access to book appointments!'
            }
          </p>
        </div>
        
        {!isIOS && deferredPrompt && (
          <button 
            className="pwa-install-btn" 
            onClick={handleInstall}
            aria-label="Install app"
          >
            <FaDownload />
            <span>Install</span>
          </button>
        )}
        
        {isIOS && (
          <div className="pwa-ios-instructions">
            <div className="pwa-ios-step">
              1. Tap <strong>Share</strong> button
            </div>
            <div className="pwa-ios-step">
              2. Select <strong>"Add to Home Screen"</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
